<template>
  <main class="p-4">

    <!-- 全局加载中 -->
    <div v-if="loading" class="text-center mt-4 text-lg font-medium">
      正在加载商品详情...
    </div>

    <!-- 毒物接口商品列表 -->
    <div
        v-if="!loading && productList.length > 1"
        class="flex flex-wrap justify-center gap-6"
    >
      <div
          v-for="product in productList"
          :key="product.spuId"
          class="bg-white rounded-xl shadow-md overflow-hidden flex flex-col items-center flex-[0_0_calc(25%-1.5rem)] max-w-[calc(25%-1.5rem)]"
      >
        <div class="relative bg-gray-100 p-4 flex justify-center items-center w-full">
          <img :src="product.images[0]" class="object-contain rounded-lg w-[240px] h-[240px]"/>
        </div>

        <div class="p-4 text-center w-full">
          <p class="text-xs font-semibold text-gray-800 mb-2">{{ product.title }}</p>
          <p class="text-md text-gray-700 mb-1">
            货号: <span class="font-bold text-lg">{{ product.number }}</span>
          </p>
          <p class="text-md text-gray-700 mb-2">
            spuId: <span class="font-bold text-lg">{{ product.spuId }}</span>
          </p>

          <button
              :disabled="product.sent"
              @click="handleNumberBack(product)"
              :class="[
              'px-3 py-1 rounded-lg text-sm transition',
              product.sent ? 'bg-gray-400 text-white cursor-not-allowed'
                           : 'bg-indigo-600 text-white hover:bg-indigo-700'
            ]"
          >
            {{ product.sent ? '已回传' : '货号回传' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 商品列表不足 2 个时显示传入的数据 -->
    <div v-if="!loading && productList.length <= 1 && passedItem" class="flex justify-center">
      <div class="bg-white rounded-xl shadow-md overflow-hidden flex flex-col items-center p-4 w-full max-w-md">
        <div class="relative bg-gray-100 flex justify-center items-center w-full h-[260px]">
          <img :src="passedItem.img" class="object-contain rounded-lg max-h-full max-w-full"/>
        </div>

        <div class="p-4 text-center w-full space-y-2">
          <p class="text-md text-gray-700 mb-1">
            标题: <span class="font-bold text-lg">{{ passedItem.title }}</span>
          </p>
          <p class="text-md text-gray-700 mb-1">
            货号: <span class="font-bold text-lg">{{ passedItem.hh }}</span>
          </p>

          <div class="flex gap-2 justify-center mt-2">
            <button
                :disabled="passedItem.sent"
                @click="handleNumberBack(passedItem)"
                :class="[
                'px-3 py-1 rounded-lg text-sm transition',
                passedItem.sent ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
              ]"
            >
              {{ passedItem.sent ? '已回传' : '货号回传' }}
            </button>

            <button
                @click="handleNoMatch"
                class="px-3 py-1 rounded-lg text-sm bg-red-500 text-white hover:bg-red-600 transition"
            >
              无匹配
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import {
  commonH5AppletDressUpFeed,
  querySpuList,
  sendNumberBack,
  getFirstDownloadUrlAndHh
} from '@/api/dewu.js'

export default {
  name: 'ProductDetail',

  props: {
    spuId: {
      type: Number,
      default: 0
    },
    queryInfo: {
      type: Object,
      default: () => ({})
    },
    refreshSearch: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      loading: true,
      firstItem: null,
      productList: [],
      type: '',
      passedItem: null,
      lastHh: '' // 🔹 上一次接口返回的货号
    }
  },

  async mounted() {
    this.initPassedItem()
    await this.refreshAll()
  },

  methods: {
    /** 初始化传入参数 */
    initPassedItem() {
      const { title, hh, img } = this.queryInfo || this.$route.query || {}
      const spuId = this.spuId || Number(this.$route.params.spuId) || 0
      if (spuId) {
        this.passedItem = { title, hh, img, spuId, sent: false }
      }
    },

    /** 清空显示数据 */
    reset() {
      this.firstItem = null
      this.productList = []
      this.passedItem = null
      this.type = ''
    },

    /** 仅刷新 firstItem（用于回传时不刷新 productList） */
    async refreshFirstItem() {
      try {
        const res = await getFirstDownloadUrlAndHh()

        // 更新 firstItem
        this.firstItem = {
          downloadUrl: res.downloadUrl || '',
          hh: res.hh || '',
          color: res.color || '',
          title: res.title || '',
          spuId: res.spuId || 0,
          sent: false
        }

      } catch (e) {
        console.error('刷新 firstItem 失败:', e)
      } finally {
        this.loading = false
      }
    },

    /** 刷新 productList */
    async refreshProductList() {
      this.loading = true
      try {
        await this.loadProductList()
      } catch(e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    /** 刷新 firstItem + productList */
    async refreshAll(refreshProductList = true) {
      try {
        await this.loadFirstItem()
        if (refreshProductList) {
          this.loading = true
          await this.loadProductList()
        }
      } finally {
        this.loading = false
      }
    },

    /** firstItem 固定接口获取 */
    async loadFirstItem() {
      try {
        const res = await getFirstDownloadUrlAndHh()
        this.firstItem = {
          downloadUrl: res.downloadUrl,
          hh: res.hh,
          color: res.color,
          title: res.title,
          spuId: res.spuId,
          cate: res.cate,
          sent: false
        }
        this.lastHh = res.hh || ''
      } catch (e) {
        console.error('加载 firstItem 失败:', e)
      }
    },

    /** 获取毒物商品列表 */
    async loadProductList() {
      const spuId = this.spuId || Number(this.$route.params.spuId || 0)
      const cate = this.firstItem.cate
      try {
        let spuIdList = [];
        if (cate?.includes('鞋')) {
          spuIdList = [spuId];
        } else {
          const feedRes = await commonH5AppletDressUpFeed('', spuId)
          const list = feedRes?.data?.correlation?.list || [];
          spuIdList = [...new Set(list.map(i => i.spuId).filter(Boolean))];
        }
        if (spuIdList.length > 1) {
          this.type = '单货号'
          const spuRes = await querySpuList(spuIdList)
          this.productList = spuRes.data.spuList.map(item => ({
            number: item.dwDesignerId,
            spuId: item.dwSpuId,
            title: item.dwSpuTitle,
            images: [item.image],
            sent: false
          }))
        } else {
          this.type = '多货号'
          this.productList = []
        }
      } catch (err) {
        console.error('加载 productList 失败:', err)
        this.productList = []
      }
    },

    /** 货号回传 */
    async handleNumberBack(item) {
      const wxLoginToken = localStorage.getItem('wxLoginToken')
      if (!wxLoginToken) return alert('请先设置 wxLoginToken')

      try {
        const number = item.hh || item.number
        await sendNumberBack(number, this.type, wxLoginToken)
        item.sent = true

        // 等待 1 秒
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 只刷新 firstItem，不刷新 productList
        await this.refreshFirstItem()

        // ✅ 只刷新 SearchBar 的 firstItem，不触发搜索
        if (this.refreshSearch) await this.refreshSearch({ onlyFirstItem: true })
      } catch (e) {
        console.error(e)
        alert('回传失败')
      }
    },

    /** 无匹配 */
    async handleNoMatch() {
      const wxLoginToken = localStorage.getItem('wxLoginToken')
      if (!wxLoginToken) return alert('请先设置 wxLoginToken')

      try {
        await sendNumberBack('', '无匹配', wxLoginToken)
        alert('无匹配已提交')

        // 等待 1 秒
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 刷新当前组件数据
        await this.refreshAll()

        // 刷新父组件 SearchBar
        if (this.refreshSearch) await this.refreshSearch()
      } catch (e) {
        console.error(e)
        alert('无匹配提交失败')
      }
    }
  }
}
</script>
